import api from "./api";

/** The catalogue: products defined once for the business, like services. */
const catalogue = "/business/products";

/** One branch's products, on that branch's terms (price, availability, stock). */
const base = (branchId) => `/business/branches/${branchId}/products`;

/** A translatable field travels as name[en] / name[ar]. */
const translations = (value) => ({ en: value?.en ?? "", ar: value?.ar ?? "" });

/** Form values -> the payload the API expects. No branch, no stock. */
const toPayload = (values) => ({
  name: translations(values.name),
  description: translations(values.description),
  price: Number(values.price),
  unit: values.unit,
  low_stock_threshold:
    values.lowStockThreshold === "" ? null : Number(values.lowStockThreshold),
  product_category_id: values.categoryId || null,
  brand_id: values.brandId || null,
  is_available_online: Boolean(values.isAvailableOnline),
  is_active: Boolean(values.isActive),
});

/* ----------------------------------------------------------------- catalogue */

/**
 * GET /business/products
 * `filters` accepts { search, category, brand, isActive }. isActive is omitted
 * when "" — false is a real value the API distinguishes from "no filter".
 */
export const fetchProducts = async ({ page = 1, perPage = 15, filters = {} } = {}) => {
  const params = { page, per_page: perPage };

  if (filters.search) params.search = filters.search;
  if (filters.category) params.category = filters.category;
  if (filters.brand) params.brand = filters.brand;
  if (filters.isActive !== "" && filters.isActive != null) params.is_active = filters.isActive;
  if (filters.lowStock) params.low_stock = 1;

  const { data } = await api.get(catalogue, { params });
  return { products: data.data.products, meta: data.meta };
};

/** The catalogue product, with `branches` — every branch that sells it. */
export const fetchProduct = async (productId) => {
  const { data } = await api.get(`${catalogue}/${productId}`);
  return data.data.product;
};

/** Creating never assigns a branch; that is syncProductBranches. */
export const createProduct = async (values) => {
  const { data } = await api.post(catalogue, toPayload(values));
  return data.data.product;
};

export const updateProduct = async (productId, values) => {
  const { data } = await api.put(`${catalogue}/${productId}`, toPayload(values));
  return data.data.product;
};

export const deleteProduct = (productId) => api.delete(`${catalogue}/${productId}`);

/**
 * PUT /business/products/{productId}/branches — the full desired set among the
 * branches this user can reach. `openingStock` is sent only for a branch new
 * to the product; the API ignores stock for one already selling it.
 */
export const syncProductBranches = async (productId, rows) => {
  const { data } = await api.put(`${catalogue}/${productId}/branches`, {
    branches: rows.map((row) => ({
      branch_id: row.branchId,
      price: Number(row.price),
      ...(row.isNew ? { opening_stock: row.openingStock === "" ? null : Number(row.openingStock) } : {}),
      // Blank = the product's own threshold.
      ...(row.threshold !== undefined
        ? { low_stock_threshold: row.threshold === "" ? null : Number(row.threshold) }
        : {}),
    })),
  });
  return data.data.product;
};

/* ------------------------------------------------------------------- branch */

/**
 * GET /business/branches/{branchId}/products — only what the branch sells.
 * `filters` accepts { search, category, brand, isActive, isAvailableOnline,
 * inStock, lowStock }; the switches are the branch's own. The tri-state
 * filters are omitted entirely when "".
 */
export const fetchBranchProducts = async (branchId, { page = 1, perPage = 15, filters = {} } = {}) => {
  const params = { page, per_page: perPage };

  if (filters.search) params.search = filters.search;
  if (filters.category) params.category = filters.category;
  if (filters.brand) params.brand = filters.brand;
  if (filters.isActive !== "" && filters.isActive != null) params.is_active = filters.isActive;
  if (filters.isAvailableOnline !== "" && filters.isAvailableOnline != null) {
    params.is_available_online = filters.isAvailableOnline;
  }
  if (filters.inStock !== "" && filters.inStock != null) params.in_stock = filters.inStock;
  if (filters.lowStock) params.low_stock = 1;

  const { data } = await api.get(base(branchId), { params });
  return { products: data.data.products, meta: data.meta };
};

export const fetchBranchProduct = async (branchId, productId) => {
  const { data } = await api.get(`${base(branchId)}/${productId}`);
  return data.data.product;
};

/** One branch's price and switches. Stock is not here — see adjustStock. */
export const updateBranchProduct = async (branchId, productId, terms) => {
  const { data } = await api.put(`${base(branchId)}/${productId}`, {
    price: Number(terms.price),
    is_available_online: Boolean(terms.isAvailableOnline),
    is_active: Boolean(terms.isActive),
  });
  return data.data.product;
};

/* -------------------------------------------------------------------- images */

/** Multipart, several files at once. Returns the product's full gallery. */
export const uploadProductImages = async (productId, files) => {
  const form = new FormData();
  files.forEach((file) => form.append("images[]", file));

  const { data } = await api.post(`${catalogue}/${productId}/images`, form);
  return data.data.images;
};

export const deleteProductImage = async (productId, imageId) => {
  const { data } = await api.delete(`${catalogue}/${productId}/images/${imageId}`);
  return data.data.images;
};

export const setPrimaryImage = async (productId, imageId) => {
  const { data } = await api.put(`${catalogue}/${productId}/images/${imageId}/primary`);
  return data.data.images;
};

/** `ids` is the gallery in its new order. */
export const reorderProductImages = async (productId, ids) => {
  const { data } = await api.put(`${catalogue}/${productId}/images/reorder`, { ids });
  return data.data.images;
};

/* --------------------------------------------------------------------- stock */

/**
 * The only way stock changes. Returns the refreshed product alongside the
 * movement that was just written.
 */
export const adjustStock = async (branchId, productId, values) => {
  const { data } = await api.post(`${base(branchId)}/${productId}/stock`, {
    type: values.type,
    quantity: Number(values.quantity),
    reason: values.reason || null,
    reference: values.reference || null,
  });
  return { product: data.data.product, movement: data.data.movement };
};

/** Paginated ledger for one product. */
export const fetchStockMovements = async (branchId, productId, page = 1) => {
  const { data } = await api.get(`${base(branchId)}/${productId}/movements`, {
    params: { page },
  });
  return { movements: data.data.movements, meta: data.meta };
};
